const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export type ExportType = 'learning-history' | 'transcript' | 'notes' | 'progress-report';

export interface ExportOptions {
  courseId?: number;
}

class ExportService {
  /**
   * Download learning history as PDF
   */
  async downloadLearningHistory(): Promise<void> {
    await this.downloadFile(
      `${API_BASE_URL}/api/export/learning-history`,
      'lernhistorie.pdf'
    );
  }

  /**
   * Download course transcript as PDF
   */
  async downloadTranscript(courseId: number): Promise<void> {
    await this.downloadFile(
      `${API_BASE_URL}/api/export/transcript/${courseId}`,
      `transkript-kurs-${courseId}.pdf`
    );
  }

  /**
   * Download notes as PDF
   * @param courseId Optional - if provided, exports only notes for this course
   */
  async downloadNotes(courseId?: number): Promise<void> {
    const url = courseId
      ? `${API_BASE_URL}/api/export/notes?courseId=${courseId}`
      : `${API_BASE_URL}/api/export/notes`;
    const filename = courseId ? `notizen-kurs-${courseId}.pdf` : 'alle-notizen.pdf';
    await this.downloadFile(url, filename);
  }

  /**
   * Download progress report as CSV
   */
  async downloadProgressReport(): Promise<void> {
    await this.downloadFile(
      `${API_BASE_URL}/api/export/progress-report`,
      'fortschrittsbericht.csv'
    );
  }

  /**
   * Generic download method for export endpoints
   */
  private async downloadFile(url: string, defaultFilename: string): Promise<void> {
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Export failed with status ${response.status}`);
      }

      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = defaultFilename;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  }

  /**
   * Get available export options based on user context
   */
  getExportOptions(): { type: ExportType; label: string; description: string }[] {
    return [
      {
        type: 'learning-history',
        label: 'Lernhistorie',
        description: 'Komplette Übersicht aller Kurse und Fortschritte',
      },
      {
        type: 'progress-report',
        label: 'Fortschrittsbericht',
        description: 'CSV-Export mit detaillierten Fortschrittsdaten',
      },
      {
        type: 'notes',
        label: 'Alle Notizen',
        description: 'Alle persönlichen Notizen als PDF',
      },
    ];
  }

  /**
   * Get course-specific export options
   */
  getCourseExportOptions(courseId: number): { type: ExportType; label: string; description: string }[] {
    return [
      {
        type: 'transcript',
        label: 'Kurs-Transkript',
        description: 'Detaillierter Bericht über diesen Kurs',
      },
      {
        type: 'notes',
        label: 'Kurs-Notizen',
        description: 'Notizen für diesen Kurs',
      },
    ];
  }
}

export const exportService = new ExportService();
export default exportService;
